<!-- Define some CSS styles for the map and layer list -->

<style>
	#map {
		height: 88vh;
	}

	.layers {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		padding: 10px;
		gap: 10px;
	}

	ul {
		list-style-type: none;
		background-color: #f1f1f1;
	}

	:global(.material-icons) {
		font-size: 21px;
	}

	:global(.leaflet-retina .leaflet-control-layers-toggle) {
		background-image: url('./assets/images/layers-2x.png');
	}

	:global(.leaflet-control-layers-toggle) {
		background-image: url('./assets/images/layers.png');
	}
</style>

<script>
	import Area from '../../../../shared/models/Area/Area';
	import AziendaEnte from '../../../../shared/models/AziendaEnte/AziendaEnte';
	import IndirizzoDipendente from '../../../../shared/models/Dipendente/IndirizzoDipendente';
	import LayerGis from '../../../../shared/models/LayerGis/LayerGis';
	import Sede from '../../../../shared/models/Sede/Sede';

	import * as L from 'leaflet';
	import 'leaflet-iconmaterial';
	import 'leaflet.featuregroup.subgroup';
	import 'leaflet.markercluster';
	import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
	import 'leaflet.markercluster/dist/MarkerCluster.css';
	import 'leaflet/dist/leaflet.css';
	import shp from 'shpjs/dist/shp.js';

	import { colorIsDifferent, getRandomColor, isValidURL } from '../../../../shared/functions';

	import { onMount } from 'svelte';
	import MapLoading from '../../../..//shared/components/common/Loading/MapLoading.svelte';
	import { hasRoleAreaManager, hasRoleCompanyManager, hasRoleSysAdmin, userSession } from './../../../../shared/stores.ts';

	// Get list of GIS layers
	const [dataLayersGis, loadingLayersGis, errorLayersGis] = LayerGis.getList();

	// Get list of companies and organizations
	const [dataAziendeEnti, loadingAziendeEnti, errorAziendeEnti] = AziendaEnte.getList();

	// Get list of employee addresses
	const [dataIndirizziDipendenti, loadingIndirizziDipendenti, errorIndirizziDipendenti] = IndirizzoDipendente.getCoordinateList();

	// Get list of office addresses
	const [dataIndirizziSedi, loadingIndirizziSedi, errorIndirizziSedi] = Sede.getCoordinateList();

	// Filter GIS layers that have attachments and create an array of layer objects
	$: arrayLayers = $dataLayersGis?.response?.filter((x) => x.allegati && x.allegati?.length > 0);

	// Create an array of names for the filtered GIS layers
	$: arrayLayersNames = $dataLayersGis?.response?.filter((x) => x.allegati && x.allegati?.length > 0).map((x) => x.descrizione);

	// Group employee addresses by office location
	$: dataIndirizziDipendentiSedi =
		$dataIndirizziDipendenti?.response?.length > 0
			? $dataIndirizziDipendenti.response?.reduce(function (r, a) {
				r[a.sedeId] = r[a.sedeId] || [];
				r[a.sedeId].push(a);
				return r;
				}, Object.create(null))
			: [];

	// Filter office addresses to get those belonging to companies/organizations
	$: dataIndirizziAziendeEnti = $dataIndirizziSedi?.response?.filter((x) => !x.scuola) || [];

	// Filter office addresses to get those belonging to schools
	$: dataIndirizziScuole = $dataIndirizziSedi?.response?.filter((x) => x.scuola) || [];

	const [dataArea, loadingArea, errorArea] = Area.get($userSession.areaId);

	// Set loadingMap to true initially
	let loadingMap = true;

	let map;
  	let currentZoom;
	let currentCenter;
	let coords = [46.4982953, 11.3547582];

	let initialZoom = 7;

	$: {
		if ($userSession.areaId) {
		if ($dataArea && $dataArea.mapZoom) {
			initialZoom = $dataArea.mapZoom;
		}
		if ($dataArea && $dataArea.mapLatitude && $dataArea.mapLongitude) {
			coords = [$dataArea.mapLatitude, $dataArea.mapLongitude];
		}
		}
	}
	
	$: dataLoaded = !$loadingLayersGis && !$loadingAziendeEnti && !$loadingIndirizziDipendenti && !$loadingIndirizziSedi && !$loadingArea;
	
	// When the component mounts, wait for a short time and then set loadingMap to false
	onMount(async () => {
		if (dataLoaded) {
			intializeMap();
		}
	});

	$: if (dataLoaded && !map) {
		intializeMap();
	}

	function intializeMap() {
		loadingMap = false;

		// Get the first office address from the list of office addresses
		const firstSede = $dataIndirizziSedi?.response?.[0];

		// Set default coordinates if no office address is available
		const defaultCoords = [46.4982953, 11.3547582];

		// Set the map center coordinates to the first office address or the default coordinates
		if (!coords || (coords[0] === 46.4982953 && coords[1] === 11.3547582)) {
			coords = firstSede ? [firstSede.latitude, firstSede.longitude] : defaultCoords;
		}
		// Create a Leaflet map object with max zoom level of 18 and set the view to the center coordinates
		map = L.map('map', {
			maxZoom: 18,
			zoomSnap: 0,
			zoomDelta: 0.25,
			wheelPxPerZoomLevel: 90
		}).setView(coords, initialZoom);

		map.on('zoomend moveend', function() {
			currentZoom = map.getZoom();
			currentCenter = map.getCenter();
		});

		if ($hasRoleAreaManager) {
			L.Control.SaveMapState = L.Control.extend({
			onAdd: function(map) {
				var btn = L.DomUtil.create('button', 'leaflet-bar leaflet-control leaflet-control-custom');
				btn.style.width = '120px';
				btn.style.height = '30px';
				btn.style.backgroundColor = 'white';
				btn.style.borderRadius = '4px';
				btn.style.cursor = 'pointer';
				btn.style.fontSize = '14px';
				btn.style.color = 'black';
				btn.innerHTML = 'Save Map State';
				L.DomEvent.on(btn, 'click', saveMapState);
				return btn;
			}
			});

			L.control.saveMapState = function(opts) {
			return new L.Control.SaveMapState(opts);
			}
			L.control.saveMapState({ position: 'topright' }).addTo(map);
		}

		// Set the tile layer URL and attribution
		const tileLayerUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
		const tileLayerAttribution = '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors';

		// Add the tile layer to the map
		L.tileLayer(tileLayerUrl, {
			attribution: tileLayerAttribution,
		}).addTo(map);

		// Define an object that maps layer types to Leaflet Material Design icons with specified colors and outlines
		const layerIcons = {
			bus_stop: L.IconMaterial.icon({
				icon: 'bus_alert',
				iconColor: '#fff',
				markerColor: '#3388ff',
				outlineColor: 'black',
				outlineWidth: 1,
			}),
			school: L.IconMaterial.icon({
				icon: 'school',
				iconColor: '#fff',
				markerColor: '#3388ff',
				outlineColor: 'black',
				outlineWidth: 1,
			}),
			domain: L.IconMaterial.icon({
				icon: 'domain',
				iconColor: '#fff',
				markerColor: '#3388ff',
				outlineColor: 'black',
				outlineWidth: 1,
			}),
			foundation: L.IconMaterial.icon({
				icon: 'foundation',
				iconColor: '#fff',
				markerColor: '#3388ff',
				outlineColor: 'black',
				outlineWidth: 1,
			}),
			person: L.IconMaterial.icon({
				icon: 'fiber_manual_record',
				iconColor: '#fff',
				markerColor: '#3388ff',
				outlineColor: 'black',
				outlineWidth: 1,
			}),
			bike: L.IconMaterial.icon({
				icon: 'bike',
				iconColor: '#fff',
				markerColor: '#3388ff',
				outlineColor: 'black',
				outlineWidth: 1,
			}),
			default: L.IconMaterial.icon({
				icon: 'location_on',
				iconColor: '#fff',
				markerColor: '#3388ff',
				outlineColor: 'black',
				outlineWidth: 1,
			}),
		};

		// Define a function that sets the map view to the clicked marker's location with zoom level of 16
		function clickZoom(e) {
			map.setView(e.target.getLatLng(), 16);
		}

		// Create a Leaflet control for layer selection with collapsed state based on whether the user has the Company Manager role
		var layerControl = L.control.layers(null, null, {
			collapsed: $hasRoleCompanyManager ? false : true,
		});

		// Define a function that returns a class name based on the count of child markers in a cluster
		const setClassName = (count) => (count < 5 ? 'small' : count < 10 ? 'medium' : 'large');

		// Define a function that creates a Leaflet DivIcon with a child count and class name based on the count
		const childIcon = (childCount) => {
			const c = ` marker-cluster-${setClassName(childCount)}`;
			return new L.DivIcon({
				html: '<div><span>' + childCount + '</span></div>',
				className: `marker-cluster ${c}`,
				iconSize: L.point(40, 40),
			});
		};

		// Create a Leaflet marker cluster group for employee markers with custom icon creation function
		var employeeParentGroup = L.markerClusterGroup({
			maxClusterRadius: 0, // Ridotto a 0 per evitare il raggruppamento
			iconCreateFunction: function (cluster) {
				const childCount = cluster.getChildCount();
				return childIcon(childCount);
			},
		}).addTo(map);

		// Create a Leaflet marker cluster group for company markers with custom icon creation function
		var companyParentGroup = L.markerClusterGroup({
			maxClusterRadius: 0, // Ridotto a 0 per evitare il raggruppamento
			iconCreateFunction: function (cluster) {
				const childCount = cluster.getChildCount();
				return childIcon(childCount);
			},
		}).addTo(map);

		// Define a function that creates a Leaflet marker with specified latitude, longitude, icon, popup content, and groupKey
		function createMarker(latitude, longitude, icon, popup, groupKey) {
			return L.marker([Number(latitude), Number(longitude)], { icon, groupKey })
				.on('click', clickZoom)
				.bindPopup(popup);
		}

		const usedColors = {};

		function getUniqueRandomColor(arrayLayers, usedColors, initialThreshold = 10, maxAttempts = 1000) {				
			let uniqueColor = getRandomColor();
			let isDifferent = false;
			let attempts = 0;
			let threshold = initialThreshold;

			// Loop until a unique color is found or the maximum number of attempts is reached
			while (!isDifferent && attempts < maxAttempts) {
				try {
					// Check if the new color is different from all existing layer colors and used colors
					isDifferent =
						arrayLayers?.every((layer) => colorIsDifferent(uniqueColor, layer?.colore, threshold)) &&
						usedColors?.every((usedColor) => colorIsDifferent(uniqueColor, usedColor, threshold));
				
				} catch (error) {
					console.error(error);
				}

				attempts++;
				
				// If the color is not unique, generate a new one and increment the attempts counter
				if (!isDifferent) {
					uniqueColor = getRandomColor();

					// Decrease the threshold as the number of attempts increases
					if (attempts % Math.floor(maxAttempts / 10) === 0 && threshold > 0) {
						threshold--;
					}
				}
			}

			return uniqueColor;
		}

		const colorsBySede = dataIndirizziAziendeEnti?.reduce((acc, { id, colore }) => {
			const uniqueColor = getUniqueRandomColor(arrayLayers, Object.values(usedColors));
			acc[id] = colore || uniqueColor;
			if (!colore) {
				usedColors[id] = uniqueColor;
			}
			return acc;
		}, {});

		function getArrayOfMarkers(overlay, sedeId = '', groupKey = '') {
			let result = [];
			switch (overlay) {
				case 'employee':
					const employees = dataIndirizziDipendentiSedi?.[sedeId] ?? [];
					result = employees.map(({ latitude, longitude, provincia, citta, cap, indirizzo, sedeId }) => {
						const popup = `Indirizzo: ${provincia ? provincia + ',' : ''} ${citta ? citta + ',' : ''} ${cap ?? ''} ${indirizzo}`;

						const icon = L.divIcon({
							className: 'employee-dot-marker',
							html: `<div style="
								width: 12px; 
								height: 12px; 
								background-color: ${colorsBySede[sedeId] || '#3388ff'}; 
								border-radius: 50%; 
								border: 2px solid black;
								box-shadow: 0 0 4px rgba(0,0,0,0.3);
							"></div>`,
							iconSize: [12, 12],
							iconAnchor: [6, 6],
							popupAnchor: [0, -6] 
						});

						return createMarker(latitude, longitude, icon, popup, groupKey);
					});
					break;
				case 'company':
					result = dataIndirizziAziendeEnti?.map(
						({ id, latitude, longitude, aziendaEnteId, denominazione, provincia, citta, indirizzo }) => {
							const nomeAzienda = !$loadingAziendeEnti
								? $dataAziendeEnti?.response?.find((x) => x.id === aziendaEnteId)?.nomeAziendaEnte ?? ''
								: '';
							const popup = `
								Nome Azienda: ${nomeAzienda}</br>
								Denominazione: ${denominazione ? denominazione + ' ' : ''}</br>
								Indirizzo: ${provincia ? provincia + ',' : ''} ${citta ? citta + ',' : ''} ${indirizzo}
							`;
							const originalIcon = layerIcons.domain;
							const icon = L.IconMaterial.icon({
								...originalIcon.options,
								markerColor: colorsBySede[id],
							});
							return createMarker(latitude, longitude, icon, popup, groupKey);
						}
					);
					break;
				case 'school':
					result = dataIndirizziScuole?.map(({ latitude, longitude, aziendaEnteId, denominazione, provincia, citta, indirizzo }) => {
						const nomeAzienda = !$loadingAziendeEnti
							? $dataAziendeEnti?.response?.find((x) => x.id === aziendaEnteId)?.nomeAziendaEnte ?? ''
							: '';
						const popup = `
							Nome Azienda: ${nomeAzienda}</br>
							Denominazione: ${denominazione ? denominazione + ' ' : ''}</br>
							Indirizzo: ${provincia ? provincia + ',' : ''} ${citta ? citta + ',' : ''} ${indirizzo}
						`;
						return createMarker(latitude, longitude, layerIcons.school, popup, groupKey);
					});
					break;
			}
			return result;
		}

		const overlays = {
			...Object.keys(dataIndirizziDipendentiSedi).reduce((acc, sedeId) => {
				const sedeCorrispondente = $dataIndirizziSedi?.response?.find(s => s.id === parseInt(sedeId));
				const nomeSede = sedeCorrispondente?.denominazione || 'Sede sconosciuta';
				
				const groupKey = `Dipendenti ${nomeSede}`;
				const layer = L.featureGroup.subGroup(employeeParentGroup, getArrayOfMarkers('employee', sedeId, '', groupKey));
				layer.options.color = colorsBySede[parseInt(sedeId)];
				return {
					...acc,
					[groupKey]: layer,
				};
			}, {}),
			...($hasRoleSysAdmin || $hasRoleAreaManager
				? 	{
						'Sedi aziendali': L.featureGroup.subGroup(companyParentGroup, getArrayOfMarkers('company', '', '')).addTo(map),
						'Sedi scolastiche': L.featureGroup.subGroup(companyParentGroup, getArrayOfMarkers('school', '', '')).addTo(map),
					}
				: 	{
						['Sedi ' + ($userSession?.scuola ? 'scolastiche' : 'aziendali')]: L.featureGroup
							.subGroup(companyParentGroup, getArrayOfMarkers($userSession?.scuola ? 'school' : 'company', '', ''))
							.addTo(map),
					}),
		};

		function createColorIcon(color) {
			const icon = document.createElement('span');
			icon.style.display = 'inline-block';
			icon.style.width = '12px';
			icon.style.height = '12px';
			icon.style.backgroundColor = color;
			icon.style.marginRight = '4px';
			icon.style.marginLeft = '8px';
			icon.style.border = '1px solid #888';
			return icon;
		}

		L.Control.ColorLayers = L.Control.Layers.extend({
			_addItem: function (obj) {
				const container = obj.overlay ? this._overlaysList : this._baseLayersList;
				const label = document.createElement('label');
				const checked = this._map.hasLayer(obj.layer);
				let input;

				if (obj.layer.getLayers) {
					input = document.createElement('input');
					input.type = 'checkbox';
					input.className = 'leaflet-control-layers-selector';
					input.defaultChecked = checked;
				} else {
					input = this._createRadioElement('leaflet-base-layers', checked);
				}

				this._layerControlInputs.push(input);
				input.layerId = L.stamp(obj.layer);
				L.DomEvent.on(input, 'click', this._onInputClick, this);

				const name = document.createElement('span');
				name.innerHTML = ' ' + obj.name;

				label.appendChild(input);
				label.appendChild(name);

				if (obj.layer.options && obj.layer.options.color) {
					const colorIcon = createColorIcon(obj.layer.options.color);
					label.appendChild(colorIcon);
				}

				if (obj.overlay) {
					container.appendChild(label);
				} else {
					container.insertBefore(label, container.firstChild);
				}

				return label;
			},
		});

		// Create a Leaflet layer control with the overlays object and add it to the map
		// Sort overlays by name - Dipendenti first, then Sedi
		const sortedOverlays = Object.entries(overlays)
		.sort(([keyA], [keyB]) => {
			// Priorità: prima i dipendenti, poi le sedi
			const isDipendentiA = keyA.startsWith('Dipendenti');
			const isDipendentiB = keyB.startsWith('Dipendenti');
			const isSediA = keyA.startsWith('Sedi');
			const isSediB = keyB.startsWith('Sedi');
			
			// Se uno è dipendente e l'altro sede, i dipendenti vengono prima
			if (isDipendentiA && isSediB) return -1;
			if (isSediA && isDipendentiB) return 1;
			
			// Se sono dello stesso tipo, ordina per numero e poi alfabeticamente
			const numA = parseInt(keyA.match(/\d+/)?.[0] ?? 0);
			const numB = parseInt(keyB.match(/\d+/)?.[0] ?? 0);
			if (numA === numB) {
				return keyA.localeCompare(keyB);
			} else {
				return numA - numB;
			}
		})
		.reduce((acc, [key, value]) => {
			acc[key] = value;
			return acc;
		}, {});

		layerControl = new L.Control.ColorLayers(null, sortedOverlays, {
			collapsed: $hasRoleCompanyManager ? false : true,
		}).addTo(map);

		
		if (arrayLayersNames && arrayLayersNames.length > 0) {
			Promise.all(
				arrayLayers?.map(async (arrayLayer) => {
					try {
						const allegatoId = arrayLayer.allegati?.[0]?.id;
						if (allegatoId) {
							const response = await fetch('api/layergis/allegato/' + allegatoId);
							// Leggi la response come ArrayBuffer invece che come JSON
							const fileData = await response.arrayBuffer();
							
							// Passa direttamente i dati binari a shp.js
							const data = await shp(fileData);
							
							const geoJson = L.geoJson(data, {
								pointToLayer: function (feature, latlng) {
									const type = feature.properties.type;
									const originalIcon = layerIcons[type] || layerIcons.default;
									const icon = L.IconMaterial.icon({
										...originalIcon.options,
										markerColor: arrayLayer.colore ? arrayLayer.colore : '#3388ff',
									});

									return L.marker(latlng, {
										icon,
									});
								},
								onEachFeature: function (feature, layer) {
									var out = [];
									if (feature.properties) {
										for (var key in feature.properties) {
											out.push(key + ': ' + feature.properties[key]);
										}
										layer.bindPopup(out.join('<br />'));
									}
								},
								style: {
									color: arrayLayer.colore ? arrayLayer.colore : '#3388ff',
								},
							});

							return {
								layer: geoJson,
								description: arrayLayer.descrizione,
							};
						}
					} catch (error) {
						console.error(`Failed to load layer: ${error.message}`);
						return null;
					}
				})
			).then((geoJsonLayers) => {
				geoJsonLayers
					.filter((item) => item)
					.sort((a, b) => {
						const numA = parseInt(a.description.match(/(\d+)/)?.[1]) || 0;
						const numB = parseInt(b.description.match(/(\d+)/)?.[1]) || 0;
						if (numA === numB) {
						return a.description.localeCompare(b.description);
						} else {
						return numA - numB;
						}
					})
					.forEach(({ layer, description }) => {
						const color = layer.options.style.color;
						const legend = document.createElement('div');
						legend.style.display = 'initial';
						const colorIcon = createColorIcon(color);
						const span = document.createElement('span');
						span.innerHTML = ' ' + description;
						legend.appendChild(span);
						legend.appendChild(colorIcon);

						layerControl.addOverlay(layer, legend.outerHTML, {
							collapsed: $hasRoleCompanyManager ? false : true,
						});
					});
				});
		}

		// Create a Leaflet control for the legend and position it at the bottom right of the map
		const legend = L.control({
			position: 'bottomright',
		});

		// Create a div element with the class 'legend'
		const div = L.DomUtil.create('div', 'legend');

		// Define an array of colors and labels for the legend
		const color = ['#6ecb39', '#f0c10c', '#f08016'];
		const label = ['0-5', '5-10', '>10'];

		// Create an empty array to hold the rows of the legend
		const rows = [];

		// Define the onAdd method for the legend control
		legend.onAdd = function () {
			// For each color in the color array, create a row with a colored square and its corresponding label
			color.map((item, index) => {
				rows.push(`
					<div class="row" style="padding: 1px">
						<i style="background: ${item}"></i>${label[index]}
					</div>  
				`);
			});
			// Set the innerHTML of the legend div to the rows array joined into a string
			div.innerHTML = rows.join('');
			return div;
		};

		// Add the legend control to the map
		legend.addTo(map);
	}


	async function saveMapState() {
		if ($userSession.areaId && currentZoom && currentCenter) {
		if ($dataArea) {
			$dataArea.mapZoom = currentZoom;
			$dataArea.mapLatitude = currentCenter.lat;
			$dataArea.mapLongitude = currentCenter.lng;
			await Area.save($dataArea);
		}
		}
	}
</script>

<!-- If the map is still loading, display a loading indicator -->
{#if loadingMap}
	<MapLoading />
{/if}

<!-- Display the map and a list of layers -->
<div id="map" class="z-0"></div>
<ul class="layers"></ul>
